import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowDown, ArrowUp, Check, Minus, Pencil, Plus, SlidersHorizontal, Trash2, X } from "lucide-react";

import {
  DETAIL_FIELDS,
  KRA_COLOURS,
  emptyKra,
  hasProposed,
  weightingTotal,
  type JDContent,
  type JDKra,
  type JDMeasure,
} from "../../utils/jobDescription";

const INPUT =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/20";
const LABEL = "mb-1 block text-xs font-semibold text-gray-600";
const colour = (i: number) => KRA_COLOURS[i % KRA_COLOURS.length];
const filled = (s: string | null | undefined) => !!s && s.trim().length > 0;

/** The line manager's editor for a job description draft.
 *
 *  Everything reads as the finished job description; one part at a time opens
 *  for editing (click it, or its pencil). Changes go straight to ``onChange``
 *  (the page saves them); Cancel puts back what was there when the part was
 *  opened. */
export default function JobDescriptionEditor({
  content,
  onChange,
}: {
  content: JDContent;
  onChange: (next: JDContent) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const snapshot = useRef<JDContent | null>(null);

  const set = (patch: Partial<JDContent>) => onChange({ ...content, ...patch });
  const setKra = (i: number, patch: Partial<JDKra>) =>
    set({ kras: content.kras.map((k, j) => (j === i ? { ...k, ...patch } : k)) });

  const openPart = (key: string) => {
    snapshot.current = content;
    setOpen(key);
  };
  const done = () => {
    snapshot.current = null;
    setOpen(null);
  };
  const cancel = () => {
    if (snapshot.current) onChange(snapshot.current);
    done();
  };
  const part = (key: string) => ({
    editing: open === key,
    onEdit: () => openPart(key),
    onDone: done,
    onCancel: cancel,
  });

  const moveKra = (i: number, d: number) => {
    const next = [...content.kras];
    const [k] = next.splice(i, 1);
    next.splice(i + d, 0, k);
    set({ kras: next });
    if (open === `kra:${i}`) setOpen(`kra:${i + d}`);
  };
  const removeKra = (i: number) => {
    const k = content.kras[i];
    const name = k.area.trim() || t("jd.ed.areaN", "Area {{n}}", { n: i + 1 });
    if (!window.confirm(t("jd.ed.removeAreaConfirm", "Remove “{{name}}” and its tasks and measures?", { name }))) return;
    set({ kras: content.kras.filter((_, j) => j !== i) });
    done();
  };
  const addKra = () => {
    snapshot.current = content;
    onChange({ ...content, kras: [...content.kras, emptyKra()] });
    setOpen(`kra:${content.kras.length}`);
  };

  const details = content.details;
  const detailsMissing = !filled(details.name) || !filled(details.job_title);
  const proposed = hasProposed(content);

  return (
    <div className="space-y-6">
      <Part
        {...part("details")}
        title={t("jd.ed.details", "Role details")}
        flag={detailsMissing ? t("jd.ed.detailsMissing", "Name and job title are needed") : undefined}
        read={
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {DETAIL_FIELDS.map((f) => {
              const value = details[f.key];
              if (f.key === "date_last_reviewed") {
                return (
                  <ReadField key={f.key} label={t(`jd.field.${f.key}`, f.label)}>
                    {filled(value) ? value : <Muted>{t("jd.ed.reviewedOnIssue", "Set to the day you issue it")}</Muted>}
                  </ReadField>
                );
              }
              return (
                <ReadField key={f.key} label={t(`jd.field.${f.key}`, f.label)}>
                  {filled(value) ? value : <Muted>{f.optional ? t("jd.ed.optionalEmpty", "Not set (optional)") : t("jd.ed.notSet", "Not set")}</Muted>}
                </ReadField>
              );
            })}
          </dl>
        }
        edit={
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DETAIL_FIELDS.filter((f) => f.key !== "date_last_reviewed").map((f, n) => (
              <label key={f.key} className="block">
                <span className={LABEL}>
                  {t(`jd.field.${f.key}`, f.label)}
                  {f.optional && <span className="ml-1 font-normal text-gray-400">{t("jd.ed.optional", "(optional)")}</span>}
                </span>
                <GrowText
                  value={details[f.key]}
                  placeholder={f.hint ? t(`jd.ed.hint.${f.key}`, f.hint) : undefined}
                  autoFocus={n === 0}
                  onChange={(v) => set({ details: { ...details, [f.key]: v } })}
                />
              </label>
            ))}
            <p className="self-end rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 sm:col-span-2">
              {t("jd.ed.reviewedHint", "Date last reviewed is filled in for you on the day you issue it. Optional rows are left off the job description when they're empty.")}
            </p>
          </div>
        }
      />

      <Part
        {...part("purpose")}
        n={1}
        title={t("jd.view.purpose", "Job purpose")}
        flag={!filled(content.purpose) ? t("jd.ed.purposeMissing", "Needed before you can issue it") : undefined}
        read={
          filled(content.purpose) ? (
            <p className="whitespace-pre-wrap leading-relaxed text-gray-800">{content.purpose}</p>
          ) : (
            <Empty>{t("jd.ed.purposeEmpty", "Two or three sentences on why the role exists and what it delivers for PME Cake.")}</Empty>
          )
        }
        edit={
          <GrowText
            value={content.purpose}
            minRows={4}
            autoFocus
            ariaLabel={t("jd.view.purpose", "Job purpose")}
            placeholder={t("jd.ed.purposeEmpty", "Two or three sentences on why the role exists and what it delivers for PME Cake.")}
            onChange={(v) => set({ purpose: v })}
          />
        }
      />

      <section>
        <SectionTitle n={2} title={t("jd.view.kras", "Key result areas")} />
        <p className="mt-1 text-xs text-gray-500">
          {t("jd.ed.krasHint", "The main parts of the job, each with a weighting, the tasks it involves and how it's measured. Click an area to edit it.")}
        </p>

        <div className="mt-3">
          <WeightingsPart
            content={content}
            {...part("weights")}
            onWeight={(i, w) => setKra(i, { weighting: w })}
            onJump={(i) => openPart(`kra:${i}`)}
          />
        </div>

        <ol className="mt-3 space-y-3">
          {content.kras.map((k, i) => (
            <KraPart
              key={i}
              kra={k}
              index={i}
              count={content.kras.length}
              {...part(`kra:${i}`)}
              onPatch={(patch) => setKra(i, patch)}
              onMove={(d) => moveKra(i, d)}
              onRemove={() => removeKra(i)}
            />
          ))}
        </ol>

        <button
          type="button"
          onClick={addKra}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-pme-blue/30 px-4 text-sm font-semibold text-pme-blue hover:border-pme-blue/60 hover:bg-pme-sky/30"
        >
          <Plus size={16} aria-hidden /> {t("jd.ed.addArea", "Add a key result area")}
        </button>

        <div className="mt-3">
          <Part
            {...part("comm")}
            title={t("jd.view.communication", "Communication & responsiveness")}
            tag={t("jd.view.notWeighted", "Not weighted")}
            dashed
            small
            read={
              <div className="space-y-2">
                {filled(content.communication.covers) ? <p className="text-gray-800">{content.communication.covers}</p> : <Empty>{t("jd.ed.commEmpty", "What good communication looks like in this role.")}</Empty>}
                {filled(content.communication.measured_by) && (
                  <ReadList title={t("jd.view.measuredBy", "Measured by")} items={[content.communication.measured_by]} />
                )}
              </div>
            }
            edit={
              <div className="space-y-3">
                <label className="block">
                  <span className={LABEL}>{t("jd.ed.covers", "What it covers")}</span>
                  <GrowText value={content.communication.covers} autoFocus onChange={(v) => set({ communication: { ...content.communication, covers: v } })} />
                </label>
                <label className="block">
                  <span className={LABEL}>{t("jd.view.measuredBy", "Measured by")}</span>
                  <GrowText value={content.communication.measured_by} onChange={(v) => set({ communication: { ...content.communication, measured_by: v } })} />
                </label>
              </div>
            }
          />
        </div>

        {proposed && (
          <div className="mt-3">
            <Part
              {...part("note")}
              title={t("jd.ed.noteTitle", "Note for measures not agreed yet")}
              small
              flag={!filled(content.proposed_note) ? t("jd.ed.noteMissing", "Say who will confirm them") : undefined}
              read={
                filled(content.proposed_note) ? (
                  <p className="text-gray-800">{content.proposed_note}</p>
                ) : (
                  <Empty>{t("jd.ed.noteEmpty", "Some measures are marked as not agreed yet. Add a line saying who will confirm them, and when.")}</Empty>
                )
              }
              edit={
                <GrowText
                  value={content.proposed_note}
                  autoFocus
                  placeholder={t("jd.ed.notePlaceholder", "e.g. Proposed measures, to be confirmed with the line manager before the first appraisal.")}
                  onChange={(v) => set({ proposed_note: v })}
                />
              }
            />
          </div>
        )}
      </section>

      <Part
        {...part("comp")}
        n={3}
        title={t("jd.view.competencies", "Behavioural competencies")}
        read={
          content.competencies.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {content.competencies.map((c, i) => (
                <li key={i} className="py-2 first:pt-0 last:pb-0 sm:flex sm:gap-4">
                  <p className="font-semibold text-gray-900 sm:w-52 sm:shrink-0">{c.name || <Muted>{t("jd.ed.unnamed", "No name yet")}</Muted>}</p>
                  <p className="text-gray-700">{c.description || <Muted>{t("jd.ed.noDescription", "No description yet")}</Muted>}</p>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>{t("jd.ed.compEmpty", "How the person is expected to work, and what good looks like in this role.")}</Empty>
          )
        }
        edit={
          <CompetencyEditor
            items={content.competencies}
            onChange={(competencies) => set({ competencies })}
          />
        }
      />

      <p className="rounded-xl bg-pme-sky/40 px-3 py-2 text-xs text-pme-navy">
        {t(
          "jd.ed.footNote",
          "Keep review dates, scoring scales and development plans out of the job description: PME People handles those in the appraisal. The performance review paragraph and the sign-off are added for you.",
        )}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- parts */

interface PartProps {
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onCancel: () => void;
}

/** One part of the job description: reads as finished text, opens to edit. */
function Part({
  n,
  title,
  tag,
  flag,
  read,
  edit,
  editing,
  onEdit,
  onDone,
  onCancel,
  dashed,
  small,
  accent,
  footerLeft,
}: PartProps & {
  n?: number | string;
  title: ReactNode;
  tag?: ReactNode;
  flag?: string;
  read: ReactNode;
  edit: ReactNode;
  dashed?: boolean;
  small?: boolean;
  accent?: string;
  footerLeft?: ReactNode;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [editing]);

  const heading = (
    <span className={`min-w-0 flex-1 font-bold text-pme-navy ${small ? "text-sm" : "text-base"}`}>
      {n != null && <span className="mr-1">{typeof n === "number" ? `${n}.` : n}</span>}
      {title}
    </span>
  );

  const border = editing ? "border-pme-blue ring-2 ring-pme-blue/15" : dashed ? "border-dashed border-gray-300" : "border-gray-200";
  const style = accent ? { borderLeft: `4px solid ${accent}` } : undefined;

  if (editing) {
    return (
      <div
        ref={ref}
        className={`rounded-xl border bg-white shadow-sm ${border}`}
        style={style}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onDone();
          }
        }}
      >
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          {heading}
          {tag && <Tag>{tag}</Tag>}
          <span className="text-xs font-semibold text-pme-blue">{t("jd.ed.editing", "Editing")}</span>
        </div>
        <div className="p-4">{edit}</div>
        <div className="flex flex-wrap items-center gap-2 rounded-b-xl border-t border-gray-100 bg-gray-50 px-4 py-2.5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">{footerLeft}</div>
          <button type="button" onClick={onCancel} className="inline-flex min-h-[40px] items-center rounded-lg px-3 text-sm font-semibold text-gray-600 hover:bg-gray-200">
            {t("jd.ed.cancel", "Cancel")}
          </button>
          <button type="button" onClick={onDone} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-pme-blue px-4 text-sm font-semibold text-white hover:bg-pme-navy">
            <Check size={15} aria-hidden /> {t("jd.ed.done", "Done")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a,button:not([data-part-edit])")) return;
        if (window.getSelection()?.toString()) return;
        onEdit();
      }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      aria-label={t("jd.ed.editPart", "Edit {{part}}", { part: typeof title === "string" ? title : "" })}
      className={`group cursor-pointer rounded-xl border text-sm transition hover:border-pme-blue/50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-pme-blue/40 ${dashed ? "bg-gray-50" : "bg-white"} ${border}`}
      style={style}
    >
      <div className={`flex items-center gap-2 px-4 ${small ? "pt-3" : "pt-3.5"}`}>
        {heading}
        {tag && <Tag>{tag}</Tag>}
        <span
          data-part-edit
          className="inline-flex min-h-[32px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-gray-400 group-hover:bg-pme-sky/50 group-hover:text-pme-blue"
          aria-hidden
        >
          <Pencil size={13} /> {t("jd.ed.edit", "Edit")}
        </span>
      </div>
      {flag && (
        <p className="mx-4 mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
          <AlertTriangle size={12} aria-hidden /> {flag}
        </p>
      )}
      <div className={`px-4 ${small ? "pb-3 pt-2" : "pb-4 pt-2"}`}>{read}</div>
    </div>
  );
}

/** The weighting bar and the list of areas, with a quick way to balance them. */
function WeightingsPart({
  content,
  editing,
  onEdit,
  onDone,
  onCancel,
  onWeight,
  onJump,
}: PartProps & {
  content: JDContent;
  onWeight: (i: number, w: number | null) => void;
  onJump: (i: number) => void;
}) {
  const { t } = useTranslation();
  const kras = content.kras;
  const total = weightingTotal(content);
  const ok = total === 100;
  const status = ok
    ? t("jd.ed.weightsOk", "Weightings add up to 100%.")
    : total > 100
      ? t("jd.ed.weightsOver", "Weightings add up to {{total}}%, {{diff}}% too much.", { total, diff: total - 100 })
      : t("jd.ed.weightsUnder", "Weightings add up to {{total}}%. {{diff}}% still to give.", { total, diff: 100 - total });

  const bar = (
    <div className="flex h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden>
      {kras.map((k, i) => (
        <span key={i} style={{ width: `${Math.min(k.weighting ?? 0, 100)}%`, background: colour(i) }} />
      ))}
    </div>
  );
  const statusLine = (
    <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-semibold ${ok ? "text-green-700" : "text-amber-700"}`} role="status">
      {ok ? <Check size={13} aria-hidden /> : <AlertTriangle size={13} aria-hidden />} {status}
    </p>
  );

  if (kras.length === 0) return null;

  if (editing) {
    return (
      <div className="rounded-xl border border-pme-blue bg-white p-4 shadow-sm ring-2 ring-pme-blue/15">
        <p className="text-sm font-bold text-pme-navy">{t("jd.ed.balanceTitle", "Balance the weightings")}</p>
        <p className="mt-0.5 text-xs text-gray-500">{t("jd.ed.balanceHint", "Together they must add up to 100%.")}</p>
        <div className="mt-3">{bar}</div>
        {statusLine}
        <ul className="mt-3 divide-y divide-gray-100">
          {kras.map((k, i) => (
            <li key={i} className="flex items-center gap-3 py-2">
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: colour(i) }} aria-hidden />
              <span className="min-w-0 flex-1 text-sm text-gray-800">{k.area || <Muted>{t("jd.ed.areaN", "Area {{n}}", { n: i + 1 })}</Muted>}</span>
              <WeightStepper value={k.weighting} onChange={(w) => onWeight(i, w)} label={k.area || t("jd.ed.areaN", "Area {{n}}", { n: i + 1 })} autoFocus={i === 0} />
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="inline-flex min-h-[40px] items-center rounded-lg px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100">
            {t("jd.ed.cancel", "Cancel")}
          </button>
          <button type="button" onClick={onDone} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-pme-blue px-4 text-sm font-semibold text-white hover:bg-pme-navy">
            <Check size={15} aria-hidden /> {t("jd.ed.done", "Done")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {bar}
      {statusLine}
      <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {kras.map((k, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => onJump(i)}
              className="flex min-h-[36px] w-full items-center gap-2 rounded-lg px-1.5 text-left text-sm hover:bg-gray-50"
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colour(i) }} aria-hidden />
              <span className="min-w-0 flex-1 truncate text-gray-800">{k.area || <Muted>{t("jd.ed.areaN", "Area {{n}}", { n: i + 1 })}</Muted>}</span>
              <span className={`shrink-0 text-sm font-bold ${k.weighting == null ? "text-amber-700" : "text-pme-navy"}`}>
                {k.weighting == null ? "—" : `${k.weighting}%`}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onEdit}
        className="mt-2 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-pme-blue hover:bg-pme-sky/40"
      >
        <SlidersHorizontal size={14} aria-hidden /> {t("jd.ed.balance", "Balance the weightings")}
      </button>
    </div>
  );
}

/** One key result area. */
function KraPart({
  kra,
  index,
  count,
  onPatch,
  onMove,
  onRemove,
  ...partProps
}: PartProps & {
  kra: JDKra;
  index: number;
  count: number;
  onPatch: (patch: Partial<JDKra>) => void;
  onMove: (d: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const tasks = kra.tasks.filter(filled);
  const measures = kra.measures.filter((m) => filled(m.text));
  const flag = !filled(kra.area)
    ? t("jd.ed.areaNeedsName", "Needs a name")
    : kra.weighting == null
      ? t("jd.ed.areaNeedsWeight", "Needs a weighting")
      : undefined;

  return (
    <li>
      <Part
        {...partProps}
        n={`2.${index + 1}`}
        title={kra.area || t("jd.ed.areaN", "Area {{n}}", { n: index + 1 })}
        tag={kra.weighting != null ? `${kra.weighting}%` : undefined}
        accent={colour(index)}
        flag={flag}
        read={
          <div className="space-y-3">
            {filled(kra.covers) ? (
              <p className="leading-relaxed text-gray-800">{kra.covers}</p>
            ) : (
              <Muted>{t("jd.ed.noCovers", "No summary of what it covers yet.")}</Muted>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <ListTitle>{t("jd.view.tasks", "Tasks")}</ListTitle>
                {tasks.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-800 marker:text-gray-400">
                    {tasks.map((task, j) => <li key={j}>{task}</li>)}
                  </ul>
                ) : (
                  <Empty small>{t("jd.ed.noTasks", "No tasks yet")}</Empty>
                )}
              </div>
              <div>
                <ListTitle>{t("jd.view.measuredBy", "Measured by")}</ListTitle>
                {measures.length > 0 ? (
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-800 marker:text-gray-400">
                    {measures.map((m, j) => (
                      <li key={j}>
                        {m.text}
                        {m.proposed && <NotAgreed />}
                      </li>
                    ))}
                  </ul>
                ) : filled(kra.measured_by) ? (
                  <p className="mt-1 text-gray-800">{kra.measured_by}</p>
                ) : (
                  <Empty small>{t("jd.ed.noMeasures", "No measures yet")}</Empty>
                )}
              </div>
            </div>
          </div>
        }
        footerLeft={
          <>
            <IconBtn label={t("jd.ed.moveUp", "Move up")} disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp size={15} /></IconBtn>
            <IconBtn label={t("jd.ed.moveDown", "Move down")} disabled={index === count - 1} onClick={() => onMove(1)}><ArrowDown size={15} /></IconBtn>
            <button type="button" onClick={onRemove} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-red-700 hover:bg-red-50">
              <Trash2 size={14} aria-hidden /> {t("jd.ed.removeArea", "Remove area")}
            </button>
          </>
        }
        edit={
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="min-w-[200px] flex-1">
                <span className={LABEL}>{t("jd.ed.areaName", "Area")}</span>
                <input
                  className={`${INPUT} font-semibold`}
                  value={kra.area}
                  autoFocus
                  placeholder={t("jd.ed.areaPlaceholder", "e.g. Supplier payments")}
                  onChange={(e) => onPatch({ area: e.target.value })}
                />
              </label>
              <div>
                <span className={LABEL}>{t("jd.ed.weighting", "Weighting")}</span>
                <WeightStepper value={kra.weighting} onChange={(w) => onPatch({ weighting: w })} label={kra.area} />
              </div>
            </div>
            <label className="block">
              <span className={LABEL}>{t("jd.ed.covers", "What it covers")}</span>
              <GrowText
                value={kra.covers}
                minRows={2}
                placeholder={t("jd.ed.coversPlaceholder", "A sentence or two on this part of the job")}
                onChange={(v) => onPatch({ covers: v })}
              />
            </label>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <TaskList
                title={t("jd.view.tasks", "Tasks")}
                addLabel={t("jd.ed.addTask", "Add a task")}
                placeholder={t("jd.ed.taskPlaceholder", "e.g. Prepare the weekly payment run")}
                items={kra.tasks}
                onChange={(tasks) => onPatch({ tasks })}
              />
              <MeasureList items={kra.measures} onChange={(measures) => onPatch({ measures })} />
            </div>
            <details className="rounded-lg bg-gray-50 px-3 py-2" open={filled(kra.measured_by) && kra.measured_by !== kra.measures[0]?.text}>
              <summary className="cursor-pointer text-xs font-semibold text-gray-600">
                {t("jd.ed.summaryMeasure", "Measure shown in the summary table")}
              </summary>
              <p className="mt-1 text-xs text-gray-500">
                {t("jd.ed.summaryMeasureHint", "One short line for the summary table at the top of the PDF.")}
              </p>
              <div className="mt-2 flex flex-wrap items-start gap-2">
                <div className="min-w-[200px] flex-1">
                  <GrowText value={kra.measured_by} onChange={(v) => onPatch({ measured_by: v })} ariaLabel={t("jd.ed.summaryMeasure", "Measure shown in the summary table")} />
                </div>
                {filled(kra.measures[0]?.text) && kra.measured_by !== kra.measures[0].text && (
                  <button
                    type="button"
                    onClick={() => onPatch({ measured_by: kra.measures[0].text })}
                    className="inline-flex min-h-[40px] items-center rounded-lg px-2 text-xs font-semibold text-pme-blue hover:bg-pme-sky/40"
                  >
                    {t("jd.ed.useFirstMeasure", "Use the first measure")}
                  </button>
                )}
              </div>
            </details>
          </div>
        }
      />
    </li>
  );
}

/* --------------------------------------------------------------- inputs */

/** A text box that grows with what's written, so nothing is cut off. */
function GrowText({
  value,
  onChange,
  placeholder,
  minRows = 1,
  autoFocus,
  ariaLabel,
  onKeyDown,
  inputRef,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
  autoFocus?: boolean;
  ariaLabel?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  inputRef?: (el: HTMLTextAreaElement | null) => void;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return (
    <textarea
      ref={(el) => {
        ref.current = el;
        inputRef?.(el);
      }}
      rows={minRows}
      className={`${INPUT} resize-none overflow-hidden leading-relaxed ${className}`}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function WeightStepper({
  value,
  onChange,
  label,
  autoFocus,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  label: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const step = (d: number) => onChange(Math.max(0, Math.min(100, (value ?? 0) + d)));
  return (
    <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white">
      <button type="button" onClick={() => step(-5)} className="flex h-10 w-9 items-center justify-center text-gray-500 hover:bg-gray-50" aria-label={t("jd.ed.less", "5% less")}>
        <Minus size={14} />
      </button>
      <div className="relative">
        <input
          className="h-10 w-16 border-x border-gray-200 pr-3 text-center text-sm font-semibold text-pme-navy focus:outline-none focus:ring-2 focus:ring-pme-blue/20"
          inputMode="numeric"
          value={value ?? ""}
          autoFocus={autoFocus}
          aria-label={t("jd.ed.weightFor", "Weighting for {{area}}, percent", { area: label })}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9]/g, "");
            onChange(v === "" ? null : Math.min(100, Number(v)));
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") { e.preventDefault(); step(1); }
            if (e.key === "ArrowDown") { e.preventDefault(); step(-1); }
          }}
        />
        <span className="pointer-events-none absolute right-1.5 top-2.5 text-xs text-gray-400">%</span>
      </div>
      <button type="button" onClick={() => step(5)} className="flex h-10 w-9 items-center justify-center text-gray-500 hover:bg-gray-50" aria-label={t("jd.ed.more", "5% more")}>
        <Plus size={14} />
      </button>
    </div>
  );
}

/** Focus a row in a list after it has been added or removed. */
function useRowFocus() {
  const refs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const pending = useRef<number | null>(null);
  useEffect(() => {
    if (pending.current == null) return;
    const el = refs.current[pending.current];
    pending.current = null;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  });
  return {
    bind: (j: number) => (el: HTMLTextAreaElement | null) => {
      refs.current[j] = el;
    },
    focusLater: (j: number) => {
      pending.current = j;
    },
  };
}

/** Enter adds the next item; Backspace in an empty item removes it. */
function rowKeys<T>(
  items: T[],
  j: number,
  isEmpty: (item: T) => boolean,
  blank: T,
  onChange: (items: T[]) => void,
  focusLater: (j: number) => void,
) {
  return (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onChange([...items.slice(0, j + 1), blank, ...items.slice(j + 1)]);
      focusLater(j + 1);
    } else if (e.key === "Backspace" && isEmpty(items[j]) && items.length > 1) {
      e.preventDefault();
      onChange(items.filter((_, n) => n !== j));
      focusLater(Math.max(0, j - 1));
    }
  };
}

function TaskList({
  title,
  addLabel,
  placeholder,
  items,
  onChange,
}: {
  title: string;
  addLabel: string;
  placeholder: string;
  items: string[];
  onChange: (items: string[]) => void;
}) {
  const { t } = useTranslation();
  const { bind, focusLater } = useRowFocus();
  return (
    <div>
      <ListTitle>{title}</ListTitle>
      <p className="mb-1.5 text-[11px] text-gray-400">{t("jd.ed.listKeys", "Enter adds the next one. Backspace on an empty line removes it.")}</p>
      <ul className="space-y-1.5">
        {items.map((item, j) => (
          <li key={j} className="flex items-start gap-2">
            <span className="mt-3.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" aria-hidden />
            <GrowText
              value={item}
              placeholder={placeholder}
              ariaLabel={`${title} ${j + 1}`}
              inputRef={bind(j)}
              onChange={(v) => onChange(items.map((x, n) => (n === j ? v : x)))}
              onKeyDown={rowKeys(items, j, (x) => !x, "", onChange, focusLater)}
            />
            <IconBtn label={t("jd.ed.removeItem", "Remove")} onClick={() => onChange(items.filter((_, n) => n !== j))}><X size={15} /></IconBtn>
          </li>
        ))}
      </ul>
      <AddBtn
        onClick={() => {
          onChange([...items, ""]);
          focusLater(items.length);
        }}
      >
        {addLabel}
      </AddBtn>
    </div>
  );
}

function MeasureList({ items, onChange }: { items: JDMeasure[]; onChange: (items: JDMeasure[]) => void }) {
  const { t } = useTranslation();
  const { bind, focusLater } = useRowFocus();
  const blank: JDMeasure = { text: "", proposed: false };
  return (
    <div>
      <ListTitle>{t("jd.view.measuredBy", "Measured by")}</ListTitle>
      <p className="mb-1.5 text-[11px] text-gray-400">{t("jd.ed.measureHint", "Tick “Not agreed yet” on a measure that still needs agreeing. It shows with a * on the job description.")}</p>
      <ul className="space-y-1.5">
        {items.map((m, j) => (
          <li key={j} className="flex items-start gap-2">
            <span className="mt-3.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" aria-hidden />
            <div className="min-w-0 flex-1">
              <GrowText
                value={m.text}
                placeholder={t("jd.ed.measurePlaceholder", "A measure, with a target where agreed")}
                ariaLabel={t("jd.ed.measureN", "Measure {{n}}", { n: j + 1 })}
                inputRef={bind(j)}
                onChange={(v) => onChange(items.map((x, n) => (n === j ? { ...x, text: v } : x)))}
                onKeyDown={rowKeys(items, j, (x) => !x.text, blank, onChange, focusLater)}
              />
              <label className="mt-1 inline-flex min-h-[28px] cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-amber-600"
                  checked={m.proposed}
                  onChange={(e) => onChange(items.map((x, n) => (n === j ? { ...x, proposed: e.target.checked } : x)))}
                />
                <span className={m.proposed ? "text-amber-800" : ""}>{t("jd.ed.notAgreed", "Not agreed yet")}</span>
              </label>
            </div>
            <IconBtn label={t("jd.ed.removeItem", "Remove")} onClick={() => onChange(items.filter((_, n) => n !== j))}><X size={15} /></IconBtn>
          </li>
        ))}
      </ul>
      <AddBtn
        onClick={() => {
          onChange([...items, blank]);
          focusLater(items.length);
        }}
      >
        {t("jd.ed.addMeasure", "Add a measure")}
      </AddBtn>
    </div>
  );
}

function CompetencyEditor({
  items,
  onChange,
}: {
  items: { name: string; description: string }[];
  onChange: (items: { name: string; description: string }[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <ul className="space-y-3">
        {items.map((c, i) => (
          <li key={i} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-2">
                <input
                  className={`${INPUT} font-semibold`}
                  value={c.name}
                  autoFocus={i === 0}
                  placeholder={t("jd.ed.compName", "Competency, e.g. Attention to detail")}
                  aria-label={t("jd.ed.compN", "Competency {{n}}", { n: i + 1 })}
                  onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <GrowText
                  value={c.description}
                  placeholder={t("jd.ed.compDescription", "What good looks like in this role")}
                  ariaLabel={t("jd.ed.compDescriptionN", "What good looks like for competency {{n}}", { n: i + 1 })}
                  onChange={(v) => onChange(items.map((x, j) => (j === i ? { ...x, description: v } : x)))}
                />
              </div>
              <IconBtn label={t("jd.ed.removeItem", "Remove")} onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={15} /></IconBtn>
            </div>
          </li>
        ))}
      </ul>
      <AddBtn onClick={() => onChange([...items, { name: "", description: "" }])}>{t("jd.ed.addComp", "Add a competency")}</AddBtn>
    </div>
  );
}

/* --------------------------------------------------------------- pieces */

function SectionTitle({ n, title }: { n: number; title: string }) {
  return (
    <h3 className="border-b-2 border-pme-blue pb-1 text-base font-bold text-pme-navy">
      {n}. {title}
    </h3>
  );
}

function ReadField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-gray-900">{children}</dd>
    </div>
  );
}

function ReadList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <ListTitle>{title}</ListTitle>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-800 marker:text-gray-400">
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}

function ListTitle({ children }: { children: ReactNode }) {
  return <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{children}</p>;
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-gray-400">{children}</span>;
}

function Empty({ children, small }: { children: ReactNode; small?: boolean }) {
  return (
    <p className={`mt-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-gray-500 ${small ? "text-xs" : "text-sm"}`}>
      {children}
    </p>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return <span className="shrink-0 rounded-full bg-pme-sky px-2.5 py-0.5 text-xs font-bold text-pme-navy">{children}</span>;
}

function NotAgreed() {
  const { t } = useTranslation();
  return (
    <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[10px] font-semibold text-amber-800">
      {t("jd.ed.notAgreed", "Not agreed yet")}
    </span>
  );
}

function AddBtn({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="mt-1.5 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-pme-blue hover:bg-pme-sky/40">
      <Plus size={14} aria-hidden /> {children}
    </button>
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
